import {Formik} from 'formik';
import {Alert, Stack, Text, TextInput, Title} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';
import {FormWithButtons} from '@flexinet/ui-components';
import {Application} from '../types';

// FNP-998: repair an application's provisioning-resource POINTERS.
//
// Owner requirement (2026-08-16): "I, as admin, should be able to go to the
// console application, fill in any missing information about source repository
// or infrastructure repository names and so on."
//
// WHY THIS EXISTS: these fields record WHICH resources an application owns.
// They are written by the provisioning orchestrator, but an application
// provisioned before a given field existed simply has no value — and the
// deprovision flow REFUSES to run without a persisted `infraStackNames`,
// because deriving a delete target is unsafe (an unfindable stack is reported
// as DELETE_COMPLETE, so a wrong name reads as a successful deletion).
//
// Every application provisioned before FNP-998 phase 1 is in that state,
// including canary-fnp711. Without this form the only fix would be writing to
// DynamoDB in prod, which the platform rules exclude.
//
// FNP-1066 adds `deploymentBucketNames` on the same footing (the field itself
// was added to the API by FNP-1015; this is the missing console half). It fails
// WORSE than a missing stack name rather than better: the teardown's
// EmptyAppBucket step treats an unrecorded bucket as non-fatal and skips, so
// the run looks healthy right up to DeleteInfraStack, which then hits a
// non-empty bucket and strands the stack in DELETE_FAILED. Both prod
// applications queued for teardown (canary-fnp711, flexinet-devops) hold 49
// objects apiece with no bucket name recorded.
//
// ⚠️ THIS IS NOT A RENAME FORM. `Application.name` is immutable (FNP-176)
// because it is the foreign-key shadow for these very resources; the API
// rejects any name change even from an admin. This edits the pointers, not the
// identity.

// FNP-1495: the ADDRESSING inputs join the pointers on this form.
//
// They were reachable by API — `buildUpdateApplicationInput` whitelists all
// five and `UpdateApplicationInput` accepts them — but no UI exposed them, so a
// mistyped zone id or certificate ARN could only be fixed by writing to
// DynamoDB, which the platform rules exclude for test and prod.
//
// ⚠️ THEY BELONG HERE, NOT ON THE CREATE FORM. FNP-1498 cut creation back to a
// name and a description deliberately: at create time no zone, certificate or
// account exists yet, and asking about them was the defect it fixed. These are
// facts about an application that already exists and, like the pointers above,
// exist to be REPAIRED. That is what FNP-998 built this form for.
export interface ProvisioningMetadataValues {
    parentDomainName: string;
    subdomain: string;
    hostedZoneId: string;
    hostedZoneName: string;
    certificateArn: string;
    sourceRepositoryName: string;
    infraRepositoryName: string;
    sourcePipelineId: string;
    infraPipelineId: string;
    infraStackNameDev: string;
    infraStackNameTest: string;
    infraStackNameProd: string;
    deploymentBucketNameDev: string;
    deploymentBucketNameTest: string;
    deploymentBucketNameProd: string;
}

interface Props {
    record: Application;
    handleSubmit: (values: Record<string, unknown>) => void;
    handleCancel: () => void;
}

export const ProvisioningMetadataForm = (
    {
        record,
        handleSubmit,
        handleCancel,
    }: Props) => {

    const initialValues: ProvisioningMetadataValues = {
        // ⚠️ `?? ''`, never `undefined`. An absent value must submit as an
        // empty string exactly as the pointers do — `putIfPresent` drops
        // `undefined`, which would make a field the admin CLEARED look
        // untouched and silently keep the old value.
        parentDomainName: (record.parentDomainName as string) ?? '',
        subdomain: (record.subdomain as string) ?? '',
        hostedZoneId: (record.hostedZoneId as string) ?? '',
        hostedZoneName: (record.hostedZoneName as string) ?? '',
        certificateArn: (record.certificateArn as string) ?? '',
        sourceRepositoryName: (record.sourceRepositoryName as string) ?? '',
        infraRepositoryName: (record.infraRepositoryName as string) ?? '',
        sourcePipelineId: (record.sourcePipelineId as string) ?? '',
        infraPipelineId: (record.infraPipelineId as string) ?? '',
        infraStackNameDev: record.infraStackNames?.dev ?? '',
        infraStackNameTest: record.infraStackNames?.test ?? '',
        infraStackNameProd: record.infraStackNames?.prod ?? '',
        deploymentBucketNameDev: record.deploymentBucketNames?.dev ?? '',
        deploymentBucketNameTest: record.deploymentBucketNames?.test ?? '',
        deploymentBucketNameProd: record.deploymentBucketNames?.prod ?? '',
    };

    const onSubmit = (values: ProvisioningMetadataValues) => {
        const {
            infraStackNameDev,
            infraStackNameTest,
            infraStackNameProd,
            deploymentBucketNameDev,
            deploymentBucketNameTest,
            deploymentBucketNameProd,
            ...rest
        } = values;

        handleSubmit({
                         ...rest,
                         // ⚠️ ALL THREE KEYS ALWAYS. StageStringMap declares
                         // dev/test/prod as non-null, so a partial map makes
                         // AppSync null the WHOLE object on read —
                         // indistinguishable from never having been written,
                         // which is the very state this form repairs.
                         infraStackNames: {
                             dev: infraStackNameDev,
                             test: infraStackNameTest,
                             prod: infraStackNameProd,
                         },
                         // FNP-1066: same all-three-keys rule. The form is the
                         // only place that can guarantee it — the service-layer
                         // encoder passes through whatever keys it is given
                         // rather than padding, deliberately, so that an absent
                         // environment is never filled in with an empty name.
                         deploymentBucketNames: {
                             dev: deploymentBucketNameDev,
                             test: deploymentBucketNameTest,
                             prod: deploymentBucketNameProd,
                         },
                     });
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {(props) => (
                <FormWithButtons
                    {...props}
                    handleCancel={handleCancel}
                >
                    <Stack gap='lg'>
                        <Alert
                            variant='light'
                            color='yellow'
                            icon={<AiOutlineWarning/>}
                            title='Provisioning facts, not the application name'
                        >
                            <Text size='sm'>
                                These fields record where this application lives and which AWS
                                resources it owns. Correcting them does not move, rename or
                                redeploy anything — it tells the platform where the existing
                                resources are. The application name itself cannot be changed.
                            </Text>
                        </Alert>

                        {/*
                          * FNP-1495: FIRST, because it is what an admin comes
                          * here to answer — "which zone is this using?" — and
                          * because the address is the fact the other sections
                          * are consequences of.
                          */}
                        <div>
                            <Title order={5} mb='xs'>Addressing</Title>
                            <Text size='sm' c='dimmed' mb='sm'>
                                Where this application is reachable, and the DNS zone and
                                certificate that serve it. Correcting a value here records
                                what is already true — it does not move DNS, reissue a
                                certificate or redeploy anything.
                            </Text>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Parent domain'
                                    description='The zone the application is addressed under'
                                    placeholder='testing.flexi.network'
                                    name='parentDomainName'
                                    value={props.values.parentDomainName}
                                    onChange={props.handleChange}
                                />
                                {/*
                                  * FNP-1568: READ-ONLY HERE, EDITABLE IN THE
                                  * PROVISION DRAWER.
                                  *
                                  * It used to be editable in both places, and
                                  * that is what made the record/run
                                  * distinction unlearnable: the operator could
                                  * not infer which screen won, because nothing
                                  * said. The drawer's value is what a
                                  * provisioning run uses, so the drawer is
                                  * where it is chosen.
                                  *
                                  * ⚠️ STILL SHOWN, deliberately. "Where does
                                  * this application live?" is the question
                                  * this tab exists to answer, and hiding the
                                  * subdomain would answer it incompletely.
                                  */}
                                <TextInput
                                    label='Subdomain'
                                    description={'The leftmost DNS label. Empty means the '
                                        + 'application is served at the parent domain itself. '
                                        + 'Set this when you provision — the Provision '
                                        + 'button asks for it.'}
                                    placeholder='Set when provisioning'
                                    name='subdomain'
                                    value={props.values.subdomain}
                                    readOnly
                                    variant='filled'
                                    data-testid='provisioning-subdomain-readonly'
                                />
                                <TextInput
                                    label='Hosted zone id'
                                    description='Route 53 zone holding the record'
                                    placeholder='Z05954072QHC7JI7Y5TXM'
                                    name='hostedZoneId'
                                    value={props.values.hostedZoneId}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Hosted zone name'
                                    placeholder='testing.flexi.network'
                                    name='hostedZoneName'
                                    value={props.values.hostedZoneName}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Certificate ARN'
                                    description={'ACM certificate serving the domain. CloudFront '
                                        + 'requires one issued in us-east-1.'}
                                    placeholder='arn:aws:acm:us-east-1:111122223333:certificate/…'
                                    name='certificateArn'
                                    value={props.values.certificateArn}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Repositories</Title>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Source repository'
                                    description='CodeCommit repository holding the application source'
                                    placeholder='my-app'
                                    name='sourceRepositoryName'
                                    value={props.values.sourceRepositoryName}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Infrastructure repository'
                                    description='CodeCommit repository holding the CDK infrastructure'
                                    placeholder='my-app-infra'
                                    name='infraRepositoryName'
                                    value={props.values.infraRepositoryName}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Pipelines</Title>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Source pipeline id'
                                    name='sourcePipelineId'
                                    value={props.values.sourcePipelineId}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Infrastructure pipeline id'
                                    name='infraPipelineId'
                                    value={props.values.infraPipelineId}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Deployed infrastructure stacks</Title>
                            <Text size='sm' c='dimmed' mb='sm'>
                                The CloudFormation stack that serves this application in each
                                environment, named <code>&lt;app&gt;-infra-&lt;stage&gt;</code>.
                                Decommissioning cannot run without the stack name for the
                                environment being torn down — it will not guess one, because a
                                stack it cannot find is indistinguishable from one already
                                deleted.
                            </Text>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Dev'
                                    placeholder='my-app-infra-dev'
                                    name='infraStackNameDev'
                                    value={props.values.infraStackNameDev}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Test'
                                    placeholder='my-app-infra-test'
                                    name='infraStackNameTest'
                                    value={props.values.infraStackNameTest}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Prod'
                                    placeholder='my-app-infra-prod'
                                    name='infraStackNameProd'
                                    value={props.values.infraStackNameProd}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Deployment buckets</Title>
                            <Text size='sm' c='dimmed' mb='sm'>
                                The S3 bucket holding this application&apos;s deployed content in
                                each environment, named{' '}
                                <code>&lt;app&gt;-infra-&lt;accountId&gt;-ui</code>. Decommissioning
                                empties this bucket before deleting the stack, and cannot do so if
                                the name is not recorded here — CloudFormation then refuses to
                                delete a non-empty bucket and the stack is left in DELETE_FAILED.
                                The platform will not derive or guess the name, for the same reason
                                it will not guess a stack name: a derived name that misses empties
                                nothing while reporting success.
                            </Text>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Dev'
                                    placeholder='my-app-infra-111122223333-ui'
                                    name='deploymentBucketNameDev'
                                    value={props.values.deploymentBucketNameDev}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Test'
                                    placeholder='my-app-infra-444455556666-ui'
                                    name='deploymentBucketNameTest'
                                    value={props.values.deploymentBucketNameTest}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Prod'
                                    placeholder='canary-fnp711-infra-477488528677-ui'
                                    name='deploymentBucketNameProd'
                                    value={props.values.deploymentBucketNameProd}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>
                    </Stack>
                </FormWithButtons>
            )}
        </Formik>
    );
};
